import React, { useState, useEffect } from 'react';

const QuantumScatterLoader = ({ onComplete, duration = 2000 }) => {
  const [stage, setStage] = useState('loading');
  const [entangled, setEntangled] = useState(false);
  const [plotFormed, setPlotFormed] = useState(false);
  const [plotComplete, setPlotComplete] = useState(false);
  const [statusText, setStatusText] = useState('Initializing Quantum Analysis...');
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate random particles with target scatter plot positions
    const scatterData = [
      { x: 80, y: 220, value: 12 },
      { x: 120, y: 180, value: 25 },
      { x: 100, y: 160, value: 18 },
      { x: 160, y: 140, value: 42 },
      { x: 140, y: 200, value: 31 },
      { x: 200, y: 120, value: 55 },
      { x: 180, y: 100, value: 48 },
      { x: 220, y: 80, value: 67 },
      { x: 240, y: 90, value: 71 },
      { x: 190, y: 150, value: 39 },
      { x: 110, y: 240, value: 8 },
      { x: 260, y: 70, value: 85 }
    ];

    const initialParticles = scatterData.map((target, i) => ({
      id: i,
      x: 150 + (Math.random() - 0.5) * 200,
      y: 150 + (Math.random() - 0.5) * 200,
      targetX: target.x,
      targetY: target.y,
      value: target.value,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      size: 4 + Math.random() * 4,
      color: `hsl(${Math.random() * 60 + 180}, 70%, 50%)`
    }));
    setParticles(initialParticles);

    // Status text sequence
    const statusSequence = [
      { time: duration * 0.15, text: 'Gathering Data...' },
      { time: duration * 0.45, text: 'Organizing Data Points...' },
      { time: duration * 0.75, text: 'Generating Insights...' },
    ];

    const statusTimers = statusSequence.map(({ time, text }) =>
      setTimeout(() => setStatusText(text), time)
    );

    // Start moving particles to scatter plot positions
    const entangleTimer = setTimeout(() => {
      setEntangled(true);
    }, duration * 0.3);

    // Particle animation loop
    const animationInterval = setInterval(() => {
      setParticles(prev => {
        if (entangled) {
          // Move particles to their scatter plot positions
          const updatedParticles = prev.map(p => {
            const dx = p.targetX - p.x;
            const dy = p.targetY - p.y;
            return {
              ...p,
              x: p.x + dx * 0.1,
              y: p.y + dy * 0.1
            };
          });
          
          // Check if all particles are close to their targets
          const allInPosition = updatedParticles.every(p => {
            const dx = p.targetX - p.x;
            const dy = p.targetY - p.y;
            return Math.sqrt(dx * dx + dy * dy) < 5;
          });
          
          if (allInPosition && !plotFormed) {
            setPlotFormed(true);
            setStatusText('Scatter Plot Generated!');
            
            // Show the blue scatter plot for 1.5 seconds before turning green
            setTimeout(() => {
              setPlotComplete(true);
              setStage('success');
              setStatusText('ANALYSIS COMPLETE');
              
              // Hold green state for 1 second before completing
              setTimeout(() => {
                setStage('complete');
                if (onComplete) onComplete();
              }, 1000);
            }, 1500);
          }
          
          return updatedParticles;
        } else {
          // Random movement during loading
          return prev.map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            vx: p.x <= 0 || p.x >= 300 ? -p.vx : p.vx,
            vy: p.y <= 0 || p.y >= 300 ? -p.vy : p.vy
          }));
        }
      });
    }, 50);

    return () => {
      statusTimers.forEach(clearTimeout);
      clearTimeout(entangleTimer);
      clearInterval(animationInterval);
    };
  }, [duration, onComplete, entangled, plotFormed]);

  const styles = {
    loadingOverlay: {
      background: 'linear-gradient(135deg, rgba(13, 17, 23, 0.95), rgba(10, 15, 25, 0.98))',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '450px',
      width: '100%',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      borderRadius: '16px',
      maxWidth: '550px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      opacity: stage === 'complete' ? 0 : 1,
      transition: 'opacity 0.5s ease-out',
    },
    quantumContainer: {
      position: 'relative',
      width: '300px',
      height: '300px',
      margin: '20px 0',
    },
    quantumField: {
      position: 'absolute',
      inset: 0,
      background: `radial-gradient(circle at center, 
        ${plotComplete ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)'} 0%, 
        transparent 70%)`,
      animation: stage !== 'complete' ? 'quantumPulse 2s ease-in-out infinite' : 'none',
      borderRadius: '50%',
      transition: 'background 0.8s ease',
    },
    statusText: {
      marginTop: '30px',
      fontSize: '16px',
      color: stage === 'success' ? '#22c55e' : '#e6edf3',
      fontWeight: '500',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      textShadow: stage === 'success' ? '0 0 10px #22c55e' : 'none',
      transition: 'all 0.3s ease-in-out',
      textAlign: 'center',
    },
    analyticsBadge: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: stage === 'success' 
        ? 'rgba(34, 197, 94, 0.15)'
        : 'rgba(59, 130, 246, 0.1)',
      color: stage === 'success' ? '#22c55e' : '#3b82f6',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      border: stage === 'success'
        ? '1px solid rgba(34, 197, 94, 0.4)'
        : '1px solid rgba(59, 130, 246, 0.3)',
      boxShadow: stage === 'success'
        ? '0 0 15px rgba(34, 197, 94, 0.3)'
        : '0 0 10px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.3s ease-in-out',
    },
    progressIndicator: {
      marginTop: '20px',
      fontSize: '12px',
      color: '#64748b',
      textAlign: 'center',
    }
  };

  const keyframes = `
    @keyframes quantumPulse {
      0%, 100% { opacity: 0.3; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }
    
    @keyframes particleRing {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1); opacity: 0.3; }
      100% { transform: scale(2); opacity: 0; }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.loadingOverlay}>
        <div style={styles.analyticsBadge}>
          LOADING ANALYTICS
        </div>
        
        <div style={styles.quantumContainer}>
          {/* Quantum field effect */}
          <div style={styles.quantumField} />
          
          {/* SVG containing the visualization */}
          <svg width="300" height="300" style={{ position: 'absolute', top: 0, left: 0 }}>
            {/* Scatter plot axes when plot is formed */}
            {plotFormed && (
              <g>
                {/* Axes with smooth transition */}
                <line x1="50" y1="270" x2="270" y2="270" stroke="#374151" strokeWidth="2" 
                      style={{ 
                        strokeDasharray: plotFormed ? 'none' : '220 220',
                        strokeDashoffset: plotFormed ? 0 : 220,
                        transition: 'stroke-dashoffset 1s ease'
                      }} />
                <line x1="50" y1="270" x2="50" y2="50" stroke="#374151" strokeWidth="2" 
                      style={{ 
                        strokeDasharray: plotFormed ? 'none' : '220 220',
                        strokeDashoffset: plotFormed ? 0 : 220,
                        transition: 'stroke-dashoffset 1s ease 0.2s'
                      }} />
                
                {/* Axis labels */}
                <text x="160" y="290" textAnchor="middle" fill="#9ca3af" fontSize="12" 
                      style={{ opacity: plotFormed ? 1 : 0, transition: 'opacity 0.8s ease 0.3s' }}>
                  Feature A
                </text>
                <text x="30" y="160" textAnchor="middle" fill="#9ca3af" fontSize="12" transform="rotate(-90, 30, 160)"
                      style={{ opacity: plotFormed ? 1 : 0, transition: 'opacity 0.8s ease 0.5s' }}>
                  Feature B
                </text>
                
                {/* Grid lines */}
                {[100, 150, 200, 250].map((x, i) => (
                  <line key={`vgrid-${x}`} x1={x} y1="50" x2={x} y2="270" stroke="#374151" strokeWidth="0.5" 
                        opacity={plotFormed ? 0.3 : 0} strokeDasharray="2 2" 
                        style={{ transition: `opacity 0.6s ease ${0.2 + i * 0.1}s` }} />
                ))}
                {[100, 150, 200, 250].map((y, i) => (
                  <line key={`hgrid-${y}`} x1="50" y1={y} x2="270" y2={y} stroke="#374151" strokeWidth="0.5" 
                        opacity={plotFormed ? 0.3 : 0} strokeDasharray="2 2" 
                        style={{ transition: `opacity 0.6s ease ${0.4 + i * 0.1}s` }} />
                ))}
              </g>
            )}
            
            {/* Connection lines when loading (quantum entanglement effect) */}
            {!entangled && particles.map((p1, i) => 
              particles.slice(i + 1).map(p2 => {
                const distance = Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
                return distance < 80 ? (
                  <line
                    key={`${p1.id}-${p2.id}`}
                    x1={p1.x}
                    y1={p1.y}
                    x2={p2.x}
                    y2={p2.y}
                    stroke="#3b82f6"
                    strokeWidth="0.5"
                    opacity="0.3"
                  />
                ) : null;
              })
            )}
            
            {/* Particles */}
            {particles.map(p => (
              <g key={p.id}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={entangled ? 6 : p.size}
                  fill={plotComplete ? '#22c55e' : (plotFormed ? '#3b82f6' : p.color)}
                  style={{
                    transition: 'all 0.8s ease',
                    filter: `drop-shadow(0 0 ${plotFormed ? 8 : p.size * 2}px ${plotComplete ? '#22c55e' : (plotFormed ? '#3b82f6' : p.color)})`
                  }}
                />
                {plotFormed && (
                  <>
                    <text
                      x={p.x}
                      y={p.y - 12}
                      textAnchor="middle"
                      fill={plotComplete ? '#22c55e' : '#3b82f6'}
                      fontSize="10"
                      fontWeight="bold"
                      style={{ 
                        opacity: plotFormed ? 1 : 0, 
                        transition: `all 0.5s ease ${p.id * 0.1}s` 
                      }}
                    >
                      {p.value}
                    </text>
                    {/* Hover ring */}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={12}
                      fill="none"
                      stroke={plotComplete ? '#22c55e' : '#3b82f6'}
                      strokeWidth="1"
                      opacity="0.3"
                      style={{
                        animation: 'particleRing 2s ease-in-out infinite',
                        animationDelay: `${p.id * 0.1}s`,
                        opacity: plotFormed ? 0.3 : 0,
                        transition: 'opacity 0.5s ease'
                      }}
                    />
                  </>
                )}
              </g>
            ))}
          </svg>
        </div>
        
        <div style={styles.statusText}>
          {statusText}
        </div>

      </div>
    </>
  );
};

export default QuantumScatterLoader;